import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Save, ImagePlus, X as XIcon } from "lucide-react";
export default function AdminMenu() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState("Food");
  const [form, setForm] = useState({ name: "", description: "", price: "", category: "", available: true });

  const load = async () => {
    const { data } = await api.get("/menu");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      // Section follows whichever tab the admin is currently viewing.
      await api.post("/menu", { ...form, section: activeTab, price: Number(form.price) });
      toast.success(`Item added to ${activeTab}`);
      setForm({ name: "", description: "", price: "", category: "", available: true });
      load();
    } catch (e) {
      toast.error("Could not add");
    }
  };

  const update = async (id, patch) => {
    await api.patch(`/menu/${id}`, patch);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this menu item?")) return;
    await api.delete(`/menu/${id}`);
    load();
  };

  // Only show items for the currently active tab, then preserve creation
  // order inside each category (backend already returns `created_at` ASC).
  const visible = items.filter((i) => (i.section || "Food") === activeTab);
  const grouped = visible.reduce((acc, i) => { (acc[i.category] ||= []).push(i); return acc; }, {});
  const counts = items.reduce((acc, i) => {
    const s = i.section === "Drinks" ? "Drinks" : "Food";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, { Food: 0, Drinks: 0 });

  return (
    <StaffLayout>
      <div className="overline text-zinc-500">{t("admin")}</div>
      <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("menu_title")}</h1>

      <div className="mt-6 flex border-b border-[#262626]" role="tablist" data-testid="admin-menu-tabs">
        {["Food", "Drinks"].map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab)}
              data-testid={`admin-menu-tab-${tab.toLowerCase()}`}
              className={`px-6 py-3 font-plex text-sm uppercase tracking-wide transition-colors ${
                isActive ? "text-white border-b-2 border-white -mb-px" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t(tab === "Food" ? "section_food" : "section_drinks")} <span className="text-zinc-500 ml-1 tabular-nums">({counts[tab]})</span>
            </button>
          );
        })}
      </div>

      <form onSubmit={create} className="mt-8 grid md:grid-cols-6 gap-3 items-end" data-testid="new-menu-form">
        <div className="md:col-span-2">
          <label className="overline text-zinc-500">{t("name_lbl")}</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-menu-name" />
        </div>
        <div>
          <label className="overline text-zinc-500">{t("price_lbl")}</label>
          <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-menu-price" />
        </div>
        <div>
          <label className="overline text-zinc-500">{t("category_lbl")}</label>
          <input list="cats" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-menu-category" />
          <datalist id="cats">
            {Object.keys(grouped).map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div className="md:col-span-2">
          <label className="overline text-zinc-500">{t("description_lbl")}</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-menu-description" />
        </div>
        <button type="submit" className="md:col-span-6 bg-white text-black px-4 py-2 font-plex text-sm hover:bg-[#007aff] hover:text-white transition-colors flex items-center justify-center gap-2" data-testid="add-menu-btn">
          <Plus size={14} /> {t("add_item_to")} {t(activeTab === "Food" ? "section_food" : "section_drinks")}
        </button>
      </form>

      {Object.keys(grouped).length === 0 ? (
        <div className="mt-10 border border-[#262626] p-8 text-center font-plex text-sm text-zinc-500" data-testid="admin-menu-empty">
          {t("no_menu_items")}
        </div>
      ) : (
        <div className="mt-10 space-y-10">
          {Object.keys(grouped).map((cat) => (
            <section key={cat}>
              <div className="overline text-zinc-500">{t("category_lbl")}</div>
              <h2 className="font-condensed text-3xl font-bold tracking-tight mt-1">{cat}</h2>
              <div className="mt-4 border border-[#262626]">
                {grouped[cat].map((i) => (
                  <MenuRow key={i.id} item={i} onUpdate={update} onRemove={remove} onReload={load} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </StaffLayout>
  );
}

function MenuRow({ item, onUpdate, onRemove, onReload }) {
  const { t } = useLang();
  const [it, setIt] = useState(item);
  const [uploading, setUploading] = useState(false);
  const [imgKey, setImgKey] = useState(Date.now());
  const fileRef = useRef(null);
  const dirty =
    it.name !== item.name ||
    it.description !== item.description ||
    Number(it.price) !== item.price ||
    it.available !== item.available;

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/menu/${item.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      setImgKey(Date.now());
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async () => {
    if (!window.confirm("Remove photo?")) return;
    try {
      await api.delete(`/menu/${item.id}/image`);
      toast.success("Photo removed");
      onReload && onReload();
    } catch {
      toast.error("Could not remove");
    }
  };

  const imgUrl = item.has_image ? `${API}/public/menu-item/${item.id}/image?v=${imgKey}` : null;

  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#262626] last:border-0 items-center" data-testid={`menu-row-${item.id}`}>
      <div className="col-span-1 relative">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`upload-menu-image-${item.id}`}
          className="w-14 h-14 border border-[#262626] bg-[#0e0e0e] flex items-center justify-center overflow-hidden hover:border-white transition-colors"
          title={imgUrl ? "Replace photo" : "Add photo"}
        >
          {imgUrl ? (
            <img src={imgUrl} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <ImagePlus size={16} className="text-zinc-500" />
          )}
          {uploading && (
            <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-[10px] font-plex text-white">…</span>
          )}
        </button>
        {imgUrl && !uploading && (
          <button
            type="button"
            onClick={removeImage}
            data-testid={`remove-menu-image-${item.id}`}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#ff3b30] text-white flex items-center justify-center border border-black"
            title="Remove photo"
          >
            <XIcon size={11} />
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          data-testid={`menu-image-input-${item.id}`}
          onChange={(e) => uploadImage(e.target.files?.[0])}
        />
      </div>
      <input value={it.name} onChange={(e) => setIt({ ...it, name: e.target.value })} className="col-span-3 px-2 py-1.5 font-plex text-sm" />
      <input value={it.description || ""} onChange={(e) => setIt({ ...it, description: e.target.value })} className="col-span-4 px-2 py-1.5 font-plex text-xs text-zinc-400" />
      <input type="number" step="0.01" value={it.price} onChange={(e) => setIt({ ...it, price: e.target.value })} className="col-span-1 px-2 py-1.5 font-plex text-sm tabular-nums" />
      <label className="col-span-1 flex items-center gap-2 text-xs font-plex text-zinc-400">
        <input type="checkbox" checked={it.available} onChange={(e) => setIt({ ...it, available: e.target.checked })} />
        avail
      </label>
      <div className="col-span-2 flex justify-end gap-2">
        <button
          disabled={!dirty}
          onClick={() => onUpdate(item.id, { name: it.name, description: it.description, price: Number(it.price), available: it.available })}
          data-testid={`save-menu-${item.id}`}
          className="border border-[#262626] px-2 py-1.5 text-xs hover:bg-white hover:text-black transition-colors disabled:opacity-30 flex items-center gap-1"
        >
          <Save size={12} /> {t("save")}
        </button>
        <button onClick={() => onRemove(item.id)} data-testid={`delete-menu-${item.id}`} className="border border-[#262626] px-2 py-1.5 text-xs hover:bg-[#ff3b30] hover:text-white hover:border-[#ff3b30] transition-colors"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}
